import {
  type InstalledPlugin,
  isListedInAllApps,
  pluginContributionKey,
} from '@crowdex/plugin-contracts';
import { Switch } from '@crowdex/ui/components/switch';

type ContributionRow = {
  key: string;
  label: string;
  detail?: string;
  /** Whether it is on; `undefined` when it follows the plugin (no switch of its own). */
  enabled?: boolean;
};

/**
 * What a plugin built from a `crowdex.json` brings. Its skills, MCP servers and agents are turned
 * on or off one by one; the rest follows the plugin.
 */
function crowdexPluginGroups(plugin: InstalledPlugin): ContributionGroup[] {
  const crowdex = plugin.crowdex;
  if (crowdex === undefined) return [];
  const on = (key: string) => !(plugin.disabledContributions ?? []).includes(key);
  const groups: ContributionGroup[] = [
    {
      title: 'Page',
      rows: crowdex.page
        ? [
            {
              key: 'page',
              label: crowdex.page.title,
              detail: isListedInAllApps({ kind: crowdex.kind, page: crowdex.page })
                ? 'In All apps'
                : 'Opened from ⌘K',
            },
          ]
        : [],
    },
    {
      title: 'Skills',
      rows: crowdex.skills.map((skill) => {
        const key = pluginContributionKey.skill(skill.name);
        return { key, label: skill.name, detail: skill.description, enabled: on(key) };
      }),
    },
    {
      title: 'MCP servers',
      rows: crowdex.mcp.map((server) => {
        const key = pluginContributionKey.mcp(server.id);
        return { key, label: server.title, enabled: on(key) };
      }),
    },
    {
      title: 'Agents',
      rows: crowdex.agents.map((agent) => {
        const key = pluginContributionKey.agent(agent.id);
        return { key, label: agent.name, enabled: on(key) };
      }),
    },
    {
      title: 'Themes',
      rows: (crowdex.themes ?? []).map((theme) => {
        const key = pluginContributionKey.theme(theme.id);
        return {
          key,
          label: theme.label,
          detail: theme.type === 'dark' ? 'Dark theme' : 'Light theme',
          enabled: on(key),
        };
      }),
    },
    {
      title: 'Actions',
      rows: crowdex.actions.map((action) => ({
        key: `action:${action.id}`,
        label: action.label,
      })),
    },
    {
      title: 'File types',
      rows: crowdex.types.map((type) => ({
        key: `type:${type.type}`,
        label: type.title,
        detail: type.type,
      })),
    },
  ];
  return groups.filter((group) => group.rows.length > 0);
}
type ContributionGroup = { title: string; rows: ContributionRow[] };

/** What an installed plugin brings, on and off, grouped by kind. */
export function pluginContentGroups(plugin: InstalledPlugin): ContributionGroup[] {
  if (plugin.crowdex !== undefined) return crowdexPluginGroups(plugin);
  const inactive = plugin.inactiveContributions;
  const rows = <T,>(
    active: readonly T[],
    off: readonly T[] | undefined,
    row: (item: T) => Omit<ContributionRow, 'enabled'>,
  ): ContributionRow[] => [
    ...active.map((item) => ({ ...row(item), enabled: true })),
    ...(off ?? []).map((item) => ({ ...row(item), enabled: false })),
  ];
  const groups: ContributionGroup[] = [
    {
      title: 'Skills',
      rows: rows(plugin.skills, inactive?.skills, (skill) => ({
        key: pluginContributionKey.skill(skill.name),
        label: skill.displayName ?? skill.name,
        detail: skill.shortDescription ?? skill.description,
      })),
    },
    {
      title: 'MCP servers',
      rows: rows(plugin.mcpServers, inactive?.mcpServers, (server) => ({
        key: pluginContributionKey.mcp(server.name),
        label: server.name,
        detail: 'url' in server ? server.url : server.command,
      })),
    },
    {
      title: 'Agents',
      rows: rows(plugin.registryAgents ?? [], inactive?.registryAgents, (agent) => ({
        key: pluginContributionKey.agent(agent),
        label: agent,
        detail: 'From the ACP registry',
      })),
    },
    {
      title: 'Resources',
      rows: rows(plugin.resources ?? [], inactive?.resources, (resource) => ({
        key: pluginContributionKey.resource(resource.id),
        label: resource.title ?? resource.id,
        ...(resource.description === undefined ? {} : { detail: resource.description }),
      })),
    },
  ];
  return groups.filter((group) => group.rows.length > 0);
}

/** What an installed plugin brings, by kind, each turned on or off on its own. */
export function PluginContents({
  plugin,
  busy,
  onToggle,
}: {
  plugin: InstalledPlugin;
  busy: boolean;
  onToggle: (plugin: InstalledPlugin, key: string, enabled: boolean) => void;
}) {
  const groups = pluginContentGroups(plugin);
  if (groups.length === 0)
    return <p className="text-muted-foreground text-sm">This plugin brings nothing listed here.</p>;
  return (
    <div className="space-y-5">
      {groups.map((group) => (
        <section key={group.title}>
          <h3 className="type-caption text-muted-foreground">{group.title}</h3>
          <div className="mt-2 divide-y divide-border overflow-hidden rounded-panel border border-border">
            {group.rows.map((row) => (
              <div className="bg-background/55" key={row.key}>
                <div className="flex items-center justify-between gap-4 px-4 py-3">
                  <span className="min-w-0">
                    <span className="block truncate text-sm">{row.label}</span>
                    {row.detail && (
                      <span className="mt-0.5 block truncate text-muted-foreground text-xs">
                        {row.detail}
                      </span>
                    )}
                  </span>
                  {row.enabled !== undefined && (
                    <span className="flex shrink-0 items-center gap-2">
                      <Switch
                        aria-label={`${row.enabled ? 'Turn off' : 'Turn on'} ${row.label}`}
                        checked={row.enabled}
                        disabled={busy}
                        onCheckedChange={(checked) => onToggle(plugin, row.key, checked)}
                      />
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
